import React, { useState, useEffect } from 'react';
import { authAPI } from '../../services/api';
import { Key, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';

interface CompanyPermission {
  id: string;
  role: string;
  resource: string;
  action: string;
  conditions?: Record<string, unknown>;
}

interface CompanyPermissionsManagerProps {
  companyId: string;
}

const CompanyPermissionsManager: React.FC<CompanyPermissionsManagerProps> = ({
  companyId,
}) => {
  const { t } = useI18n();
  const [permissions, setPermissions] = useState<CompanyPermission[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    role: '',
    resource: '',
    action: '',
    conditionsText: '',
  });

  const resources = ['clients', 'appointments', 'employees', 'services', 'products', 'properties', 'financial', 'reports'];
  const actions = ['read', 'create', 'update', 'delete'];

  const loadPermissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.get(`/companies/${companyId}/permissions`);
      setPermissions(response.data || []);
    } catch (err: any) {
      console.error('[CompanyPermissionsManager] Failed to load permissions', err);
      setError(t('rbac.permissions.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPermissions();
  }, [companyId]);

  const resetForm = () => {
    setForm({ role: '', resource: '', action: '', conditionsText: '' });
    setEditingId(null);
    setError(null);
    setMessage(null);
  };

  const handleEdit = (permission: CompanyPermission) => {
    setForm({
      role: permission.role,
      resource: permission.resource,
      action: permission.action,
      conditionsText: permission.conditions
        ? JSON.stringify(permission.conditions, null, 2)
        : '',
    });
    setEditingId(permission.id);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      let conditions: Record<string, unknown> = {};
      if (form.conditionsText.trim()) {
        conditions = JSON.parse(form.conditionsText);
      }

      const payload = {
        role: form.role,
        resource: form.resource,
        action: form.action,
        conditions,
      };

      await authAPI.put(`/admin/companies/${companyId}/permissions`, payload);

      setMessage('Permissão salva com sucesso!');
      resetForm();
      await loadPermissions();
    } catch (err: any) {
      console.error('[CompanyPermissionsManager] Failed to save permission', err);
      setError(err.response?.data?.error || 'Não foi possível salvar permissão.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (permissionId: string, role: string, resource: string, action: string) => {
    if (!confirm('Tem certeza que deseja excluir esta permissão?')) {
      return;
    }

    try {
      await authAPI.delete(
        `/admin/companies/${companyId}/permissions/roles/${role}/resources/${resource}/actions/${action}`
      );
      setMessage('Permissão excluída com sucesso!');
      await loadPermissions();
    } catch (err: any) {
      console.error('[CompanyPermissionsManager] Failed to delete permission', err);
      setError(err.response?.data?.error || 'Não foi possível excluir permissão.');
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>{t('common.loading')}</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>{t('rbac.roles.permissions')}</h3>
        {!editingId && (
          <button
            onClick={() => setEditingId('new')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            <Plus size={16} />{t('rbac.permissions.new')}</button>
        )}
      </div>

      {error && (
        <div
          style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}

      {message && (
        <div
          style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          {message}
        </div>
      )}

      {editingId && (
        <form onSubmit={handleSave} style={{ marginBottom: '24px', padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <h4 style={{ marginTop: 0 }}>{editingId === 'new' ? 'Nova Permissão' : 'Editar Permissão'}</h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                Role
              </label>
              <input
                type="text"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
                placeholder="admin, manager, employee"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                Recurso
              </label>
              <select
                value={form.resource}
                onChange={(e) => setForm({ ...form, resource: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              >
                <option value="">{t('placeholders.select')}</option>
                {resources.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                Ação
              </label>
              <select
                value={form.action}
                onChange={(e) => setForm({ ...form, action: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              >
                <option value="">{t('placeholders.select')}</option>
                {actions.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>{t('rbac.permissions.conditionsOptional')}</label>
            <textarea
              value={form.conditionsText}
              onChange={(e) => setForm({ ...form, conditionsText: e.target.value })}
              rows={4}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'monospace',
              }}
              placeholder='{"zone_id": "user_zone_id", "status": "pending"}'
            />
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: saving ? 0.6 : 1,
              }}
            >
              <Save size={16} />
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#6b7280',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              <X size={16} />{t('actions.cancel')}</button>
          </div>
        </form>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Role</th>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>{t('common.resource')}</th>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>{t('common.action')}</th>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>{t('rbac.permissions.conditions')}</th>
              <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {permissions.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>{t('rbac.permissions.noneConfigured')}</td>
              </tr>
            ) : (
              permissions.map((perm) => (
                <tr key={perm.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px', fontSize: '14px' }}>{perm.role}</td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>{perm.resource}</td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>{perm.action}</td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    {perm.conditions && Object.keys(perm.conditions).length > 0
                      ? JSON.stringify(perm.conditions)
                      : '-'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleEdit(perm)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#f3f4f6',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                        title={t('actions.edit')}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(perm.id, perm.role, perm.resource, perm.action)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#fee2e2',
                          border: '1px solid #fecaca',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                        title={t('common.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyPermissionsManager;

